import { Button } from "@/Button";
import { Header } from "@/Header";
import { ValidateInput } from "@/Input";
// import { ValidateSelect } from "@/Select";
import { ValidateSelectList } from "@/SelectList";
import { SequentialAnimation } from "@/SequentialAnimation";
import { SignupContext } from "@/SignupContext";
import { ValidateTextarea } from "@/Textarea";
import { ValidateUploader } from "@/Uploader";
import { Dialog } from "@/Dialog";
import { useAppColor } from "^/common/useAppColor";
import { useUserCreatePetForm, useUserPetCreate } from "~/services/auth";
import { transformKeyValueArrayToOptions } from "%/dataTransform";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useContext, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { ValidateControllerSelect } from "@/ControllerSelect";
import { useLoadingStore } from "../../stores/loading";
import {
  canUseResourceBridge,
  uploadResourceViaBridge,
} from "%/appResourceUpload";

export const Route = createFileRoute("/sign-up/pet")({
  component: RouteComponent,
});

interface PetFormValues {
  petImg: string | File;
  petName: string;
  petRegNumber: string;
  petType: string;
  gender: string;
  year: string;
  month: string;
  day: string;
  petWeight: string;
  neuterYn: string;
  inoculation: string;
  personality: string;
  allergy: string;
  memo: string;
  userName: string;
  userSeq: number | undefined;
}

const Template = () => {
  const { getInfo } = useContext(SignupContext);
  const { mutate: createPet } = useUserPetCreate();
  const { data: petFormData } = useUserCreatePetForm();
  const { toggleLoading } = useLoadingStore();
  const [showValidationErrorDialog, setShowValidationErrorDialog] =
    useState(false);
  const [validationErrorMessage, setValidationErrorMessage] = useState("");
  // 앱(웹뷰)에서는 네이티브 브릿지로 이미지를 먼저 업로드 → fileSeq 사용 (구버전 WebView 업로드 실패 우회)
  const useBridge = canUseResourceBridge();
  const [bridgeFileSeq, setBridgeFileSeq] = useState<number | null>(null);
  const [bridgePreview, setBridgePreview] = useState<string>("");
  const [bridgeUploading, setBridgeUploading] = useState(false);
  // const [test, setTest] = useState("");
  const info = useMemo(() => {
    const info = getInfo();
    // console.log(info);
    return info;
  }, [getInfo]);
  const {
    handleSubmit,
    control,
    formState: { isValid, errors },
    watch,
  } = useForm<PetFormValues>({
    defaultValues: {
      petImg: "",
      petName: "",
      petRegNumber: "",
      petType: "",
      gender: "",
      year: "",
      month: "",
      day: "",
      petWeight: "",
      neuterYn: "",
      inoculation: "",
      personality: "",
      allergy: "",
      memo: "",
      userName: info?.name,
      userSeq: info?.userSeq,
    },
    mode: "onChange",
  });
  const { history } = useRouter();
  console.log(isValid);

  const bindSubmit = async (data: PetFormValues) => {
    console.log("Pet form data:", data);
    console.log("data.petImg:", data.petImg);
    console.log("data.petImg type:", typeof data.petImg);
    console.log("data.petImg instanceof File:", data.petImg instanceof File);
    // setTest(JSON.stringify(data));
    console.log(petFormData);
    console.log(info);
    const inoculation = petFormData?.inoculations.map((item) => {
      if (data.inoculation.includes(item.cdInoculation)) {
        return {
          ...item,
          activeYn: "Y",
        };
      } else {
        return {
          ...item,
          activeYn: "N",
        };
      }
    });
    const personality = petFormData?.personalities.map((item) => {
      if (data.personality.includes(item.cdPersonality)) {
        return {
          ...item,
          activeYn: "Y",
        };
      } else {
        return {
          ...item,
          activeYn: "N",
        };
      }
    });
    console.log(inoculation);
    // API 형태에 맞게 데이터 변환
    const petCreateData = {
      ...data,
      userName: info?.userName,
      inoculations: inoculation,
      personalities: personality,
      userSeq: info?.userSeq,
      mainPetYn: "N",
      // 앱 브릿지로 먼저 올린 경우 그 fileSeq 사용, 아니면 0(서버가 file 파트로 처리)
      fileSeq: useBridge && bridgeFileSeq ? bridgeFileSeq : 0,
      // 브릿지 경로에서는 파일을 다시 올리지 않는다(fileSeq 로 전달됨)
      petImg: useBridge && bridgeFileSeq ? "" : data.petImg,
    };
    // setTest(JSON.stringify(petCreateData));
    // alert(JSON.stringify(petCreateData));
    toggleLoading(true);
    createPet(petCreateData, {
      onSuccess: (response) => {
        // console.log("펫 생성 성공:", response);
        // addToast("반려동물 정보가 저장되었습니다.");
        if (response.statusCode === 200) {
          history.push("/sign-up/customer-done");
        }
        toggleLoading(false);
      },
      onError: (error) => {
        console.error("펫 생성 실패:", error);
        // addToast("반려동물 정보 저장에 실패했습니다.");
        toggleLoading(false);
      },
    });
  };

  const handleSkip = () => {
    history.push("/sign-up/customer-done");
  };

  useAppColor({
    background: "#ff9934",
  });

  // 연도 선택 옵션 생성 (현재 연도부터 20년 전까지)
  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    Array.from({ length: 20 }, (_, i) => currentYear - i).map;
    return Array.from({ length: 20 }, (_, i) => currentYear - i);
  }, []);

  // 월 선택 옵션 생성 (1-12월)
  const monthOptions = useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => i + 1);
  }, []);

  // 일 선택 옵션 생성 (1-31일)
  const dayOptions = useMemo(() => {
    const selectedYear = parseInt(watch("year"));
    const selectedMonth = parseInt(watch("month"));

    if (!selectedYear || !selectedMonth)
      return Array.from({ length: 31 }, (_, i) => i + 1);

    const lastDay = new Date(selectedYear, selectedMonth, 0).getDate();
    return Array.from({ length: lastDay }, (_, i) => i + 1);
  }, [watch("year"), watch("month")]);

  // API 데이터를 옵션 형태로 변환
  const petBreedOptions = useMemo(() => {
    if (!petFormData?.petBreeds) return [];
    return transformKeyValueArrayToOptions(petFormData.petBreeds);
  }, [petFormData?.petBreeds]);

  const personalityOptions = useMemo(() => {
    if (!petFormData?.personalities) return [];
    return petFormData.personalities.map((item) => ({
      label: item.personalityName,
      value: item.cdPersonality,
    }));
  }, [petFormData?.personalities]);

  const inoculationOptions = useMemo(() => {
    if (!petFormData?.inoculations) return [];
    return petFormData.inoculations.map((item) => ({
      label: item.inoculationName,
      value: item.cdInoculation,
    }));
  }, [petFormData?.inoculations]);

  return (
    <div className="flex flex-col flex-auto">
      <Header
        title="반려동물 등록정보"
        className="static text-white border-[0px]"
      />
      <div className="bg-white rounded-t-3xl">
        <form
          onSubmit={handleSubmit(bindSubmit)}
          className="bg-white  flex-col flex-auto rounded-t-3xl py-10 px-5 shadow-[0_-15px_60px_-15px_rgba(0,0,0,0.3)] gap-4 grid grid-cols-1"
        >
          <SequentialAnimation>
            <ValidateInput
              className="w-full"
              label="보호자 이름"
              disabled
              value={info?.userName}
            />
            <div>
              {useBridge ? (
                <div className="w-full">
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    반려동물 사진 등록 (선택)
                  </label>
                  <button
                    type="button"
                    disabled={bridgeUploading}
                    onClick={async () => {
                      try {
                        setBridgeUploading(true);
                        const r = await uploadResourceViaBridge(
                          "/api/user/auth/upload-image",
                        );
                        setBridgeFileSeq(r.fileSeq);
                        setBridgePreview(r.fileUrl);
                      } catch (e) {
                        setValidationErrorMessage(
                          e instanceof Error
                            ? e.message
                            : "사진 업로드에 실패했습니다.",
                        );
                        setShowValidationErrorDialog(true);
                      } finally {
                        setBridgeUploading(false);
                      }
                    }}
                    className="flex h-44 w-full items-center justify-center overflow-hidden rounded-lg border border-dashed border-gray-300 bg-gray-50 disabled:opacity-60"
                  >
                    {bridgePreview ? (
                      <img
                        src={bridgePreview}
                        alt="반려동물 사진"
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <span className="text-sm text-gray-500">
                        {bridgeUploading ? "업로드 중..." : "사진 선택하기"}
                      </span>
                    )}
                  </button>
                </div>
              ) : (
                <Controller
                  name="petImg"
                  control={control}
                  render={({ field }) => (
                    <ValidateUploader
                      onUploaded={field.onChange}
                      onValidationError={(error) => {
                        setValidationErrorMessage(error);
                        setShowValidationErrorDialog(true);
                      }}
                      errorMessage={errors?.petImg?.message}
                      className="w-full"
                      label="반려동물 사진 등록 (선택)"
                      maxSizeMB={10}
                    />
                  )}
                />
              )}
            </div>
            <Controller
              name="petName"
              control={control}
              rules={{ required: "반려동물 이름을 입력하세요" }}
              render={({ field }) => (
                <ValidateInput
                  {...field}
                  className="w-full"
                  label="반려동물 이름"
                  errorMessage={errors?.petName?.message}
                />
              )}
            />
            <Controller
              name="petRegNumber"
              control={control}
              render={({ field }) => (
                <ValidateInput
                  {...field}
                  className="w-full"
                  label="국가동물등록번호(선택)"
                />
              )}
            />
            <Controller
              name="petType"
              control={control}
              rules={{ required: "반려동물 품종을 선택하세요" }}
              render={({ field }) => (
                <ValidateSelectList
                  onChange={(selectedOption) =>
                    field.onChange(selectedOption?.value || "")
                  }
                  label="반려동물 품종"
                  placeholder="품종을 선택하세요"
                  required={true}
                  isSearchable={true}
                  errorMessage={errors?.petType?.message}
                  options={petBreedOptions}
                />
              )}
            />
            <Controller
              name="gender"
              control={control}
              rules={{ required: "성별을 선택하세요" }}
              render={({ field }) => (
                <ValidateControllerSelect
                  value={field.value ? field.value.split(",") : []}
                  onChange={(value) => field.onChange(value.join(","))}
                  label="성별"
                  required={true}
                  max={1}
                  col={2}
                  errorMessage={errors?.gender?.message}
                  options={[
                    { label: "암컷", value: "FEMALE" },
                    { label: "수컷", value: "MALE" },
                  ]}
                />
              )}
            />
            <div>
              <label className="block mb-2 font-medium text-gray-900">
                반려동물 나이 <span className="text-orange-500">*</span>
              </label>
              <div className="flex gap-2 w-full">
                <Controller
                  name="year"
                  control={control}
                  rules={{ required: "연도를 입력하세요" }}
                  render={({ field }) => (
                    <ValidateSelectList
                      onChange={(selectedOption) =>
                        field.onChange(selectedOption?.value || "")
                      }
                      isDefaultValue={false}
                      placeholder="년도"
                      isSearchable={false}
                      options={yearOptions.map((year) => ({
                        value: String(year),
                        label: `${year}년`,
                      }))}
                      className="h-14 text-lg text-center"
                    />
                  )}
                />
                <Controller
                  name="month"
                  control={control}
                  rules={{ required: "월을 입력하세요" }}
                  render={({ field }) => (
                    <ValidateSelectList
                      onChange={(selectedOption) =>
                        field.onChange(selectedOption?.value || "")
                      }
                      isDefaultValue={false}
                      placeholder="00월"
                      isSearchable={false}
                      options={monthOptions.map((month) => ({
                        value: month.toString().padStart(2, "0"),
                        label: `${month}월`,
                      }))}
                      className="h-14 text-lg text-center"
                    />
                  )}
                />
                <Controller
                  name="day"
                  control={control}
                  rules={{ required: "일을 입력하세요" }}
                  render={({ field }) => (
                    <ValidateSelectList
                      onChange={(selectedOption) =>
                        field.onChange(selectedOption?.value || "")
                      }
                      isDefaultValue={false}
                      placeholder="00일"
                      isSearchable={false}
                      options={dayOptions.map((day) => ({
                        value: String(day),
                        label: `${day.toString().padStart(2, "0")}일`,
                      }))}
                      className="h-14 text-lg text-center"
                    />
                  )}
                />
              </div>
            </div>

            {/* <ValidateDateInput label="반려동물 나이" required={true} /> */}
            <div className="relative pr-6 w-fit">
              <Controller
                name="petWeight"
                control={control}
                rules={{ required: "반려동물 몸무게를 입력하세요" }}
                render={({ field }) => (
                  <ValidateInput
                    {...field}
                    type="number"
                    inputMode="decimal"
                    label="반려동물 몸무게"
                    required={true}
                    errorMessage={errors?.petWeight?.message}
                  />
                )}
              />
              <span className="absolute right-0 bottom-0">kg</span>
            </div>
            <Controller
              name="neuterYn"
              control={control}
              rules={{ required: "중성화 여부를 선택하세요" }}
              render={({ field }) => (
                <ValidateControllerSelect
                  value={field.value ? field.value.split(",") : []}
                  onChange={(value) => field.onChange(value.join(","))}
                  label="중성화"
                  required={true}
                  col={2}
                  max={1}
                  errorMessage={errors?.neuterYn?.message}
                  options={[
                    { label: "중성화 완료", value: "Y" },
                    { label: "중성화 예정/미완료", value: "N" },
                  ]}
                />
              )}
            />
            <Controller
              name="inoculation"
              control={control}
              rules={{ required: "접종 정보를 선택하세요" }}
              render={({ field }) => (
                <ValidateControllerSelect
                  value={field.value ? field.value.split(",") : []}
                  onChange={(value) => field.onChange(value.join(","))}
                  label="최근 1년 이내 접종 정보"
                  required={true}
                  errorMessage={errors?.inoculation?.message}
                  options={inoculationOptions}
                  textClassName="text-[12px]"
                />
              )}
            />
            <Controller
              name="personality"
              control={control}
              rules={{ required: "성격/성향을 선택하세요" }}
              render={({ field }) => (
                <ValidateControllerSelect
                  value={field.value ? field.value.split(",") : []}
                  onChange={(value) => field.onChange(value.join(","))}
                  label="해당하는 성격/성향을 모두 선택해주세요"
                  required={true}
                  errorMessage={errors?.personality?.message}
                  options={personalityOptions}
                  textClassName="text-[12px]"
                />
              )}
            />
            <Controller
              name="allergy"
              control={control}
              render={({ field }) => (
                <ValidateTextarea
                  {...field}
                  className="w-full"
                  label="알레르기 정보를 알려주세요"
                />
              )}
            />
            <Controller
              name="memo"
              control={control}
              render={({ field }) => (
                <ValidateTextarea
                  {...field}
                  className="w-full"
                  label="기타 메모"
                />
              )}
            />
          </SequentialAnimation>
          {/* <div>{test}</div> */}
        </form>
        <div className="bg-white">
          <div className="px-4 py-6 w-full bg-white">
            <Button
              className="w-full bg-white"
              style={{ pointerEvents: "auto" }}
              onClick={handleSkip}
              disabled={false}
            >
              <div className="py-3 mx-auto w-full text-base font-semibold text-center text-black bg-[#CCCCCC] rounded-full">
                건너뛰기
              </div>
            </Button>
          </div>
          <div className="px-4 py-6 w-full bg-white">
            <Button
              className="w-full bg-white"
              style={{ pointerEvents: "auto" }}
              onClick={handleSubmit(bindSubmit)}
              disabled={!isValid}
            >
              <div className="py-3 mx-auto w-full text-base font-semibold text-center text-white bg-orange-400 rounded-full">
                반려동물 정보 저장하기
              </div>
            </Button>
          </div>
        </div>
      </div>
      {/* <ThemedFixedButton
        disabled={false}
        onClick={handleSkip}
        color="secondary"
        size="md"
      >
        건너뛰기
      </ThemedFixedButton>
      <ThemedFixedButton
        disabled={!isValid}
        onClick={handleSubmit(bindSubmit)}
        color="primary"
        size="md"
      >
        반려동물 정보 저장하기
      </ThemedFixedButton> */}

      {/* 파일 검증 에러 Dialog */}
      <Dialog
        visible={showValidationErrorDialog}
        onClose={() => setShowValidationErrorDialog(false)}
      >
        <div className="text-center">
          <h3 className="mb-2 text-lg font-semibold">파일 형식 오류</h3>
          <p className="mb-4 text-gray-600">{validationErrorMessage}</p>
          <p className="mb-4 text-sm text-gray-500">
            이미지 파일만 업로드 가능합니다. (JPG, PNG, GIF, WEBP, BMP, SVG)
          </p>
          <button
            onClick={() => setShowValidationErrorDialog(false)}
            className="px-4 py-2 text-white bg-orange-500 rounded-lg transition-colors hover:bg-orange-600"
          >
            확인
          </button>
        </div>
      </Dialog>
    </div>
  );
};
function RouteComponent() {
  return <Template />;
}
